import axios, {
    InternalAxiosRequestConfig,
    AxiosResponse,
    AxiosRequestConfig,
    AxiosInstance,
} from "axios";
import { API_BASE_URL } from "../constants/api";

// --- Custom API Error Class ---
class ApiError extends Error {
    public status?: number;
    public data?: any;

    constructor(message: string, status?: number, data?: any) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.data = data;

        if ((Error as any).captureStackTrace) {
            (Error as any).captureStackTrace(this, ApiError);
        }
    }
}

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 0,
    headers: {
        "Content-Type": "application/json",
    },
});

// Add token in headers
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        const token = localStorage.getItem("authToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// --- RESPONSE INTERCEPTOR: FIXED REDIRECTS ---
api.interceptors.response.use(
    (response: AxiosResponse) => response,

    async (error: any) => {
        const { response } = error;

        // If server responded at all
        if (response) {
            console.error(`API Error (${response.status}):`, response.data);

            // 🔥 FIX: 401 behavior based on "role"
            if (response.status === 401) {
                const role = localStorage.getItem("role");

                localStorage.removeItem("authToken");
                localStorage.removeItem("user");

                if (role === "admin") {
                    window.location.href = "/app/admin";
                } else if (role === "applicant") {
                    window.location.href = "/app/login";
                } else {
                    // anonymous → do not redirect to /admin
                    console.warn("Anonymous user unauthorized — staying on page");
                }

                return Promise.reject(
                    new ApiError("Unauthorized access.", response.status, response.data)
                );
            }

            // Other errors (400–500)
            return Promise.reject(
                new ApiError(
                    response.data?.message || `Request failed with status ${response.status}`,
                    response.status,
                    response.data
                )
            );
        }

        // No response from server
        if (error.request) {
            console.error("Network/Timeout:", error.message);
            return Promise.reject(new ApiError("Network or Server Unreachable.", 0, error));
        }

        // Client error
        console.error("Axios Setup Error:", error.message);
        return Promise.reject(new ApiError(`Client-side Error: ${error.message}`));
    }
);

// --- Exposed Wrapper Methods ---
const httpService = {
    get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        const response = await api.get<T>(url, config);
        return response.data;
    },

    post: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
        const response = await api.post<T>(url, data, config);
        return response.data;
    },

    put: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
        const response = await api.put<T>(url, data, config);
        return response.data;
    },

    patch: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
        const response = await api.patch<T>(url, data, config);
        return response.data;
    },

    delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        const response = await api.delete<T>(url, config);
        return response.data;
    },
};

export default httpService;
export { ApiError };
